import { useSelector } from 'react-redux';
/**
 * Custom useMultiStyleConfig hook for CustomSelect component
 * Returns style objects for different parts of the select component
 * @param {string} componentName - Name of the component (should be 'CustomSelect')
 * @param {Object} [props] - Component props (can include size, variant, colorScheme, etc.)
 * @returns {Object} Style objects for field, menu, option, label
 */
const useMultiStyleConfig = (componentName) => {
    const { darkMode } = useSelector((state) => state.settings);
    // Define fallback values for CSS variables
    const cssVarWithFallback = (varName, fallback) => {
        if (
            typeof window !== 'undefined' &&
            window.CSS &&
            window.CSS.supports
        ) {
            return `var(${varName}, ${fallback})`;
        }
        return `var(${varName})`;
    };

    if (componentName === 'CustomSelect') {
        return {
            // Button/Field styles
            field: {
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: '50ms',
                paddingInlineStart: cssVarWithFallback(
                    '--chakra-space-4',
                    '1rem',
                ),
                paddingInlineEnd: cssVarWithFallback(
                    '--chakra-space-4',
                    '1rem',
                ),
                height: cssVarWithFallback('--chakra-sizes-10', '2.5rem'),
                fontSize: cssVarWithFallback('--chakra-fontSizes-md', '1rem'),
                borderRadius: cssVarWithFallback(
                    '--chakra-radii-md',
                    '0.375rem',
                ),
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: cssVarWithFallback(
                    darkMode
                        ? '--chakra-colors-dark-tcap-borderGray'
                        : '--chakra-colors-border',
                    darkMode ? '#565557' : '#e2e8f0',
                ),
                background: cssVarWithFallback(
                    darkMode
                        ? '--chakra-colors-dark-bgDark'
                        : '--chakra-colors-white',
                    darkMode ? '#1e2021' : '#ffffff',
                ),
                cursor: 'pointer',
                outline: 'none',
                position: 'relative',
                _hover: {
                    borderColor: cssVarWithFallback(
                        '--chakra-colors-gray-300',
                        '#cbd5e0',
                    ),
                },
                _focus: {
                    borderColor: cssVarWithFallback(
                        '--chakra-colors-brand-500',
                        'rgb(49, 130, 206)',
                    ),
                    boxShadow: `0 0 0 1px ${cssVarWithFallback('--chakra-colors-brand-500', 'rgb(49, 130, 206)')}`,
                },
                '&[aria-expanded="true"]': {
                    borderColor: cssVarWithFallback(
                        '--chakra-colors-brand-500',
                        'rgb(49, 130, 206)',
                    ),
                    boxShadow: `0 0 0 1px ${cssVarWithFallback('--chakra-colors-brand-500', 'rgb(49, 130, 206)')}`,
                },
                '&[data-focus-visible-added="true"]': {
                    borderColor: cssVarWithFallback(
                        '--chakra-colors-brand-500',
                        'rgb(49, 130, 206)',
                    ),
                    boxShadow: `0 0 0 1px ${cssVarWithFallback('--chakra-colors-brand-500', 'rgb(49, 130, 206)')}`,
                },
                _disabled: {
                    opacity: 0.4,
                    cursor: 'not-allowed',
                },
            },
            // Dropdown menu styles
            menu: {
                minWidth: cssVarWithFallback('--chakra-sizes-3xs', '14rem'),
                fontSize: cssVarWithFallback('--chakra-fontSizes-md', '1rem'),
                borderRadius: cssVarWithFallback(
                    '--chakra-radii-md',
                    '0.375rem',
                ),
                background: cssVarWithFallback(
                    darkMode
                        ? '--chakra-colors-dark-tcap-weak'
                        : '--chakra-colors-white',
                    darkMode ? '#1e1f21' : '#ffffff',
                ),
                boxShadow: cssVarWithFallback(
                    '--chakra-shadows-sm',
                    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                ),
                color: 'inherit',
                paddingTop: cssVarWithFallback('--chakra-space-2', '0.5rem'),
                paddingBottom: cssVarWithFallback('--chakra-space-2', '0.5rem'),
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: cssVarWithFallback(
                    darkMode
                        ? '--chakra-colors-dark-tcap-borderGray'
                        : '--chakra-colors-border',
                    darkMode ? '#565557' : '#e2e8f0',
                ),
                listStyle: 'none',
                margin: 0,
                padding: `${cssVarWithFallback('--chakra-space-2', '0.5rem')} 0`,
                maxHeight: '300px',
                overflowY: 'auto',
                outline: 'none',
            },
            // Option item styles
            option: {
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                paddingInlineStart: cssVarWithFallback(
                    '--chakra-space-4',
                    '1rem',
                ),
                paddingInlineEnd: cssVarWithFallback(
                    '--chakra-space-4',
                    '1rem',
                ),
                height: cssVarWithFallback('--chakra-sizes-10', '2.5rem'),
                fontSize: cssVarWithFallback('--chakra-fontSizes-md', '1rem'),
                transition: 'background 0.1s',
                _hover: {
                    background: cssVarWithFallback(
                        '--chakra-colors-gray-100',
                        '#f7fafc',
                    ),
                },
                '&[data-highlighted="true"]': {
                    background: cssVarWithFallback(
                        '--chakra-colors-gray-100',
                        '#f7fafc',
                    ),
                },
                '&[aria-selected="true"]': {
                    background: cssVarWithFallback(
                        '--chakra-colors-gray-50',
                        '#f9fafb',
                    ),
                    fontWeight: cssVarWithFallback(
                        '--chakra-fontWeights-medium',
                        '500',
                    ),
                },
            },
            // Label styles (if needed)
            label: {
                display: 'block',
                textAlign: 'start',
                fontSize: cssVarWithFallback(
                    '--chakra-fontSizes-sm',
                    '0.875rem',
                ),
                marginInlineEnd: cssVarWithFallback(
                    '--chakra-space-3',
                    '0.75rem',
                ),
                marginBottom: cssVarWithFallback(
                    '--chakra-space-1-5',
                    '0.375rem',
                ),
                fontWeight: cssVarWithFallback(
                    '--chakra-fontWeights-medium',
                    '500',
                ),
                transitionProperty: cssVarWithFallback(
                    '--chakra-transition-property-common',
                    'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                ),
                transitionDuration: cssVarWithFallback(
                    '--chakra-transition-duration-normal',
                    '200ms',
                ),
                opacity: 1,
                color: cssVarWithFallback(
                    '--chakra-colors-fg-emphasized',
                    '#1a202c',
                ),
            },
        };
    }

    // Return empty object for other components
    return {};
};

export { useMultiStyleConfig };